import {useMemo, useState} from 'react'
import {Card, Form, Row, Col, Spinner, Button} from 'react-bootstrap'
import {useQuery} from '@apollo/client'
import axios from 'axios'
import {toast} from 'react-toastify'
import {TWILIO_NUMBERS_USAGE_REPORT} from '../../gql/queries/twilioBilling'
import {useTableSort} from '../../helpers/useTableSort'
import {URL as BACKEND_URL} from '../../gql/client'
import {STORAGE_NAMES} from '../../helpers/consts'

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(STORAGE_NAMES.TOKEN)}`,
  'Content-Type': 'application/json',
})

const money = (n: number | null | undefined) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`

const formatE164 = (e164: string) => {
  const m = (e164 || '').match(/^\+1(\d{3})(\d{3})(\d{4})$/)
  return m ? `+1 (${m[1]}) ${m[2]}-${m[3]}` : e164
}

const CLASS_BADGE: Record<string, string> = {
  pool: 'badge-light-primary',
  policydial: 'badge-light-success',
  messaging: 'badge-light-info',
  unclassified: 'badge-light-warning',
}
const CLASS_LABEL: Record<string, string> = {
  pool: 'Pool',
  policydial: 'PolicyDial',
  messaging: 'Messaging',
  unclassified: 'Unclassified',
}

const PAGE_SIZE = 25

// Build the last 6 month options (value = "YYYY-M").
const monthOptions = (() => {
  const out: {value: string; label: string}[] = []
  const now = new Date()
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    out.push({
      value: `${d.getFullYear()}-${d.getMonth() + 1}`,
      label: d.toLocaleString('en-US', {month: 'long', year: 'numeric'}),
    })
  }
  return out
})()

// Admin: per-number voice consumption + Twilio cost for a month (all owned numbers).
export const UsageByNumberTab = () => {
  const [period, setPeriod] = useState(monthOptions[0].value)
  const [year, month] = period.split('-').map(Number)
  const [rebuilding, setRebuilding] = useState(false)

  const rebuildUsage = async () => {
    setRebuilding(true)
    try {
      const res = await axios.post<{queued: number}>(
        `${BACKEND_URL}/api/telephony/supervisor/usage-rebuild?year=${year}&month=${month}`,
        {},
        {headers: authHeaders()}
      )
      toast.success(
        `Queued ${res.data?.queued ?? 0} day(s) — data is compiling in the background, reopen in ~1 min.`
      )
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Could not rebuild usage data')
    } finally {
      setRebuilding(false)
    }
  }
  const {data, loading} = useQuery(TWILIO_NUMBERS_USAGE_REPORT, {
    variables: {year, month},
    fetchPolicy: 'cache-and-network',
  })
  const report = data?.twilioNumbersUsageReport

  const [classFilter, setClassFilter] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const {onSort, sortRows, arrow} = useTableSort<any>('cost', 'desc')

  const rows = useMemo(() => {
    let r: any[] = report?.numbers ?? []
    if (classFilter) r = r.filter((x) => x.classification === classFilter)
    if (search.trim()) {
      const q = search.replace(/[^0-9]/g, '')
      r = r.filter((x) => (x.phone_number || '').replace(/[^0-9]/g, '').includes(q))
    }
    return sortRows(r)
  }, [report, classFilter, search, sortRows])

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const current = Math.min(page, pageCount)
  const pageRows = rows.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE)

  return (
    <div className='mt-5'>
      <div className='mb-5 d-flex justify-content-between align-items-start flex-wrap gap-3'>
        <div>
          <h2 className='mb-1'>Consumption by Number</h2>
          <span className='text-muted'>
            Voice usage &amp; Twilio cost per number — every owned number (pool, PolicyDial, messaging).
          </span>
          <div className='text-muted fs-8 mt-1'>
            Compiled in the background from Twilio. A fresh month may be empty until the first build —
            click “Rebuild data”, then reopen in ~1 min.
          </div>
        </div>
        <div className='d-flex gap-2 align-items-start'>
          <Form.Select
            style={{maxWidth: 220}}
            value={period}
            onChange={(e: any) => {
              setPeriod(e.target.value)
              setPage(1)
            }}
          >
            {monthOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
          <Button
            variant='light'
            onClick={rebuildUsage}
            disabled={rebuilding}
            title='Recompile this month’s usage from Twilio (runs in the background)'
          >
            {rebuilding ? 'Queuing…' : 'Rebuild data'}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <Row className='g-4 mb-5'>
        {[
          {label: 'Numbers with activity', value: report?.count ?? '—'},
          {label: 'Total calls', value: report?.total_calls ?? '—'},
          {label: 'Total minutes', value: report ? report.total_min : '—'},
          {label: 'Total Twilio cost', value: report ? money(report.total_cost) : '—'},
        ].map((c) => (
          <Col md={3} sm={6} key={c.label}>
            <Card className='h-100'>
              <Card.Body className='py-4'>
                <div className='fs-2 fw-bolder'>{c.value}</div>
                <div className='text-muted fs-7'>{c.label}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card>
        <Card.Header className='d-flex align-items-center justify-content-between flex-wrap gap-3'>
          <Card.Title className='mb-0'>By number</Card.Title>
          <div className='d-flex gap-2 flex-wrap'>
            <Form.Select
              size='sm'
              style={{maxWidth: 160}}
              value={classFilter}
              onChange={(e: any) => {
                setClassFilter(e.target.value)
                setPage(1)
              }}
            >
              <option value=''>All types</option>
              <option value='pool'>Pool</option>
              <option value='policydial'>PolicyDial</option>
              <option value='messaging'>Messaging</option>
              <option value='unclassified'>Unclassified</option>
            </Form.Select>
            <Form.Control
              size='sm'
              style={{maxWidth: 200}}
              placeholder='Search number…'
              value={search}
              onChange={(e: any) => {
                setSearch(e.target.value)
                setPage(1)
              }}
            />
          </div>
        </Card.Header>
        <Card.Body className='p-0'>
          {loading && !report ? (
            <div className='text-center py-10'>
              <Spinner animation='border' size='sm' />
            </div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-dashed align-middle gy-2 mb-0'>
                <thead>
                  <tr className='fw-bold text-muted' style={{cursor: 'pointer'}}>
                    <th className='ps-6' onClick={() => onSort('phone_number')}>Number{arrow('phone_number')}</th>
                    <th onClick={() => onSort('classification')}>Type{arrow('classification')}</th>
                    <th onClick={() => onSort('owner')}>Owner{arrow('owner')}</th>
                    <th className='text-end' onClick={() => onSort('inbound_calls')}>In{arrow('inbound_calls')}</th>
                    <th className='text-end' onClick={() => onSort('outbound_calls')}>Out{arrow('outbound_calls')}</th>
                    <th className='text-end' onClick={() => onSort('total_calls')}>Total{arrow('total_calls')}</th>
                    <th className='text-end' onClick={() => onSort('total_min')}>Minutes{arrow('total_min')}</th>
                    <th className='text-end pe-6' onClick={() => onSort('cost')}>Cost{arrow('cost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r: any) => (
                    <tr key={r.phone_number}>
                      <td className='ps-6'>
                        <div className='fw-bold'>{formatE164(r.phone_number)}</div>
                        {r.friendly_name && r.friendly_name !== r.phone_number && (
                          <div className='text-muted fs-8'>{r.friendly_name}</div>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${CLASS_BADGE[r.classification] || 'badge-light'}`}>
                          {CLASS_LABEL[r.classification] || r.classification}
                        </span>
                      </td>
                      <td className='text-muted'>{r.owner || '—'}</td>
                      <td className='text-end'>{r.inbound_calls}</td>
                      <td className='text-end'>{r.outbound_calls}</td>
                      <td className='text-end fw-semibold'>{r.total_calls}</td>
                      <td className='text-end text-muted'>{r.total_min}</td>
                      <td className='text-end fw-semibold pe-6'>{money(r.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length === 0 && (
                <p className='text-muted mb-0 p-6'>No numbers with activity for this period.</p>
              )}
            </div>
          )}
        </Card.Body>
        {pageCount > 1 && (
          <Card.Footer className='d-flex align-items-center justify-content-between'>
            <span className='text-muted fs-7'>
              {rows.length} numbers · page {current} of {pageCount}
            </span>
            <div className='d-flex gap-2'>
              <button
                className='btn btn-sm btn-light'
                disabled={current <= 1}
                onClick={() => setPage(current - 1)}
              >
                Prev
              </button>
              <button
                className='btn btn-sm btn-light'
                disabled={current >= pageCount}
                onClick={() => setPage(current + 1)}
              >
                Next
              </button>
            </div>
          </Card.Footer>
        )}
      </Card>
    </div>
  )
}
